import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash, Phone, EnvelopeSimple, ArrowSquareOut, PencilSimple, Briefcase, Heart } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Semaforo, StatusBadge , EstadoBadge } from "../components/Semaforo";
import { formatDate, formatTime, formatLabel } from "../lib/format";
import { MemberForm, memberFormDefault, authImageUrl } from "../components/MemberForm";
import PersonSilhouette from "../components/PersonSilhouette";
import { TabLabel } from "../components/DetailHelpers";
import { useConfirm } from "../components/ConfirmDialog";

export default function AuthorityDetail({ entityType, listRoute }) {
  const confirm = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(memberFormDefault());
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      setError(null);
      const { data } = await api.get(`/authorities/${id}`);
      setData(data);
    } catch (e) {
      const status = e?.response?.status;
      setError(status === 404 ? "not_found" : "error");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (error === "not_found") {
    return (
      <div className="p-12 max-w-xl mx-auto text-center" data-testid="authority-not-found">
        <div className="font-display text-2xl font-bold text-slate-900 mb-2">
          Autoridad no encontrada
        </div>
        <p className="text-sm text-slate-500 mb-6">
          Es posible que haya sido eliminada o que su enlace esté desactualizado tras la
          actualización del directorio. Vuelve al listado y selecciónala nuevamente.
        </p>
        <Link to={listRoute}>
          <Button>Volver al listado</Button>
        </Link>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-12 text-center text-slate-500">
        Error al cargar. <button className="underline" onClick={load}>Reintentar</button>
      </div>
    );
  }
  if (!data) return <div className="p-8 text-slate-500">Cargando…</div>;

  const { authority, members, commitments, projects, meetings } = data;

  const openCreate = () => {
    setEditingId(null);
    setForm(memberFormDefault());
    setOpen(true);
  };

  const openEdit = (m) => {
    setEditingId(m.id);
    setForm({
      role: m.role || "",
      name: m.name || "",
      adhesion_politica: m.adhesion_politica || "",
      profesion: m.profesion || "",
      email: m.email || "",
      telefono: m.telefono || "",
      foto_url: m.foto_url || "",
      empresa: m.empresa || "",
      cargo: m.cargo || "",
      areas_interes: m.areas_interes || [],
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name || !form.role) {
      toast.error("Nombre y cargo son obligatorios");
      return;
    }
    try {
      if (editingId) {
        await api.put(`/authority_members/${editingId}`, form);
        toast.success("Integrante actualizado");
      } else {
        await api.post(`/authorities/${id}/members`, form);
        toast.success("Integrante agregado");
      }
      setOpen(false);
      setForm(memberFormDefault());
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };

  const removeMember = async (mid) => {
    if (!(await confirm({ title: "Eliminar integrante", description: "¿Deseas eliminar a este integrante? Esta acción no se puede deshacer.", danger: true }))) return;
    try {
      await api.delete(`/authority_members/${mid}`);
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div data-testid="authority-detail-page">
      <PageHeader
        title={authority.name}
        description={`${authority.tipo_vinculo} · ${authority.region || ""} ${authority.comuna ? `· ${authority.comuna}` : ""}`}
        actions={<Link to={listRoute}><Button variant="outline">Volver</Button></Link>}
      />

      <div className="px-6 py-6">
        <Tabs defaultValue="members">
          <TabsList>
            <TabsTrigger value="members"><TabLabel label={`Integrantes (${members.length})`} hasData={members.length > 0} /></TabsTrigger>
            <TabsTrigger value="commits"><TabLabel label={`Compromisos (${commitments.length})`} hasData={commitments.length > 0} /></TabsTrigger>
            <TabsTrigger value="projects"><TabLabel label={`Proyectos (${projects.length})`} hasData={projects.length > 0} /></TabsTrigger>
            <TabsTrigger value="meetings"><TabLabel label={`Reuniones (${meetings.length})`} hasData={meetings.length > 0} /></TabsTrigger>
          </TabsList>

          <TabsContent value="members">
            <Card className="rounded-md mt-4">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle>Integrantes / cargos</CardTitle>
                <Button size="sm" className="bg-slate-900 text-white" onClick={openCreate} data-testid="add-member-btn">
                  <Plus size={14} className="mr-1" /> Agregar
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {members.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">Sin integrantes registrados.</div>
                ) : (
                  <ul className="divide-y divide-slate-100" data-testid="members-list">
                    {members.map((m) => (
                      <li key={m.id} data-testid={`member-row-${m.id}`} className="px-5 py-4 flex items-start gap-4">
                        <div className="h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 flex-shrink-0">
                          {m.foto_url ? (
                            <img src={authImageUrl(m.foto_url)} alt={m.name} className="h-full w-full object-cover" />
                          ) : (
                            <PersonSilhouette size={56} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="font-display font-bold text-slate-900">{m.name}</span>
                            <span className="text-xs uppercase tracking-wider text-slate-500">
                              {m.role.replace("_", " ")}
                            </span>
                            {m.adhesion_politica && (
                              <span className="text-xs px-1.5 py-0.5 bg-slate-100 rounded text-slate-700">
                                {m.adhesion_politica}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                            {m.profesion && (
                              <span className="flex items-center gap-1"><Briefcase size={12} />{m.profesion}</span>
                            )}
                            {m.email && (
                              <a href={`mailto:${m.email}`} className="flex items-center gap-1 hover:text-slate-900">
                                <EnvelopeSimple size={12} />{m.email}
                              </a>
                            )}
                            {m.telefono && (
                              <span className="flex items-center gap-1"><Phone size={12} />{m.telefono}</span>
                            )}
                            {entityType === "organizacion" && (m.empresa || m.cargo) && (
                              <span className="flex items-center gap-1 text-slate-400">
                                {[m.empresa, m.cargo].filter(Boolean).join(" · ")}
                              </span>
                            )}
                          </div>
                          {Array.isArray(m.areas_interes) && m.areas_interes.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                              <Heart size={12} className="text-rose-500 flex-shrink-0" />
                              {m.areas_interes.map((a, i) => (
                                <span key={i} data-testid={`member-area-${m.id}-${i}`} className="text-[11px] px-2 py-0.5 bg-rose-50 text-rose-700 border border-rose-100 rounded-full">
                                  {a}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button size="sm" variant="ghost" data-testid={`edit-member-${m.id}`} onClick={() => openEdit(m)}>
                            <PencilSimple size={14} className="text-slate-600" />
                          </Button>
                          <Button size="sm" variant="ghost" data-testid={`delete-member-${m.id}`} onClick={() => removeMember(m.id)}>
                            <Trash size={14} className="text-red-600" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="commits">
            <Card className="rounded-md mt-4">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-base">Compromisos vinculados</CardTitle>
                <div className="flex items-center gap-2">
                  <Link to={`/compromisos?related_entity_type=authority&related_entity_id=${id}&new=1`} data-testid="auth-new-commitment">
                    <Button size="sm" className="bg-slate-900"><Plus size={14} className="mr-1" /> Nuevo compromiso</Button>
                  </Link>
                  <Link to={`/compromisos?related_entity_type=authority&related_entity_id=${id}`} className="text-xs text-slate-600 hover:underline">Gestionar</Link>
                </div>
              </CardHeader>
              <CardContent className="p-0"><Table>
              <TableHeader><TableRow><TableHead>Compromiso</TableHead><TableHead>Tipo</TableHead><TableHead>Estado</TableHead><TableHead>Fecha</TableHead><TableHead>Semáforo</TableHead></TableRow></TableHeader>
              <TableBody>
                {commitments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500">Sin compromisos.</TableCell></TableRow>}
                {commitments.map(c => (
                  <TableRow key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/compromisos/${c.id}`)}>
                    <TableCell className="font-medium">
                      <Link to={`/compromisos/${c.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{c.name}</Link>
                    </TableCell>
                    <TableCell><StatusBadge tone="info">{c.tipo}</StatusBadge></TableCell>
                    <TableCell><EstadoBadge value={c.estado} /></TableCell>
                    <TableCell>{formatDate(c.fecha_compromiso)}</TableCell>
                    <TableCell><Semaforo value={c.semaforo} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></CardContent></Card>
          </TabsContent>

          <TabsContent value="projects">
            <Card className="rounded-md mt-4">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-base">Proyectos</CardTitle>
                <div className="flex items-center gap-2">
                  <Link to={`/proyectos?related_entity_type=authority&related_entity_id=${id}&new=1`} data-testid="auth-new-project">
                    <Button size="sm" className="bg-slate-900"><Plus size={14} className="mr-1" /> Nuevo proyecto</Button>
                  </Link>
                  <Link to={`/proyectos?related_entity_type=authority&related_entity_id=${id}`} className="text-xs text-slate-600 hover:underline">Gestionar</Link>
                </div>
              </CardHeader>
              <CardContent className="p-0"><Table>
              <TableHeader><TableRow><TableHead>Proyecto</TableHead><TableHead>Eje</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
              <TableBody>
                {projects.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-slate-500">Sin proyectos.</TableCell></TableRow>}
                {projects.map(p => (
                  <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/proyectos/${p.id}`)}>
                    <TableCell><Link to={`/proyectos/${p.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{p.name}</Link></TableCell>
                    <TableCell>{p.eje_estrategico || "—"}</TableCell>
                    <TableCell><EstadoBadge value={p.estado} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></CardContent></Card>
          </TabsContent>

          <TabsContent value="meetings">
            <Card className="rounded-md mt-4">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-base">Reuniones</CardTitle>
                <div className="flex items-center gap-2">
                  <Link to={`/reuniones?related_entity_type=authority&related_entity_id=${id}&new=1`} data-testid="auth-new-meeting">
                    <Button size="sm" className="bg-slate-900"><Plus size={14} className="mr-1" /> Nueva reunión</Button>
                  </Link>
                  <Link to={`/reuniones?related_entity_type=authority&related_entity_id=${id}`} className="text-xs text-slate-600 hover:underline">Gestionar</Link>
                </div>
              </CardHeader>
              <CardContent className="p-0"><Table>
              <TableHeader><TableRow><TableHead>Asunto</TableHead><TableHead>Fecha</TableHead><TableHead>Lugar</TableHead><TableHead>Tipo</TableHead><TableHead>Estado</TableHead><TableHead className="w-12 text-right"><span className="sr-only">Acciones</span></TableHead></TableRow></TableHeader>
              <TableBody>
                {meetings.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">Sin reuniones.</TableCell></TableRow>}
                {meetings.map(m => (
                  <TableRow key={m.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/reuniones/${m.id}`)}>
                    <TableCell className="font-medium">
                      <Link to={`/reuniones/${m.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{m.asunto}</Link>
                      {m.acuerdos && <div className="text-xs text-slate-500 truncate max-w-md">{m.acuerdos.slice(0, 90)}{m.acuerdos.length > 90 ? "…" : ""}</div>}
                    </TableCell>
                    <TableCell>{formatDate(m.fecha)}{m.hora && <span className="text-xs text-slate-500 ml-1">{formatTime(m.hora)}</span>}</TableCell>
                    <TableCell>{m.lugar || "—"}</TableCell>
                    <TableCell>{m.tipo || "—"}</TableCell>
                    <TableCell><StatusBadge tone="info">{m.estado_seguimiento}</StatusBadge></TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Link to={`/reuniones/${m.id}`}><Button size="sm" variant="ghost"><ArrowSquareOut size={14} /></Button></Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Member dialog (create + edit) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="member-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar integrante" : "Nuevo integrante"}</DialogTitle>
            <DialogDescription>
              Completa los datos personales y áreas de interés. La foto y áreas de interés ayudan a
              identificar afinidades para compromisos y proyectos.
            </DialogDescription>
          </DialogHeader>
          <MemberForm form={form} setForm={setForm} entityType={entityType} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} data-testid="save-member-btn" className="bg-slate-900 text-white">
              {editingId ? "Guardar cambios" : "Crear integrante"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


